import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

// Convert a base64 string to ArrayBuffer for VAPID key
function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray.buffer as ArrayBuffer;
}

const getVapidPublicKey = async (): Promise<string | null> => {
  try {
    const { data, error } = await supabase.functions.invoke('get-vapid-key');

    if (error) {
      console.error('Error fetching VAPID key:', error);
      return null;
    }

    return data?.vapidPublicKey || null;
  } catch (error) {
    console.error('Error in getVapidPublicKey:', error);
    return null;
  }
};

const registerSubscription = async (
  userId: string,
  sub: PushSubscription
): Promise<boolean> => {
  try {
    const subscriptionJSON = sub.toJSON();

    if (!subscriptionJSON.keys?.p256dh || !subscriptionJSON.keys?.auth) {
      console.error('Subscription keys are missing');
      return false;
    }

    const { error } = await supabase.functions.invoke('register-token', {
      body: {
        user_id: userId,
        token: sub.endpoint,
        platform: 'web',
        subscription_data: {
          endpoint: subscriptionJSON.endpoint,
          keys: {
            p256dh: subscriptionJSON.keys.p256dh,
            auth: subscriptionJSON.keys.auth
          },
          expirationTime: subscriptionJSON.expirationTime
        },
        updated_at: new Date().toISOString()
      },
    });

    if (error) {
      console.error('Error registering subscription:', error);
      return false;
    }

    console.log('Push subscription registered successfully');
    return true;
  } catch (error) {
    console.error('Error in registerSubscription:', error);
    return false;
  }
};

export const usePushNotifications = () => {
  const { user } = useAuth();
  const [permissionStatus, setPermissionStatus] = useState<NotificationPermission | null>(null);
  const [isSupported, setIsSupported] = useState<boolean>(false);
  const [serviceWorker, setServiceWorker] = useState<ServiceWorkerRegistration | null>(null);
  const [isRegistering, setIsRegistering] = useState<boolean>(false);
  const [subscription, setSubscription] = useState<PushSubscription | null>(null);

  useEffect(() => {
    if ('Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window) {
      setIsSupported(true);
      setPermissionStatus(Notification.permission);

      const checkExistingSubscription = async () => {
        try {
          const registration = await navigator.serviceWorker.getRegistration();
          if (registration) {
            setServiceWorker(registration);
            const pm = (registration as any).pushManager;
            if (pm) {
              const existingSub = await pm.getSubscription();
              if (existingSub) {
                setSubscription(existingSub);
                console.log('Existing push subscription found');
              }
            }
          }
        } catch (error) {
          console.error('Error checking existing subscription:', error);
        }
      };

      if (Notification.permission === 'granted') {
        checkExistingSubscription();
      }
    } else {
      setIsSupported(false);
    }
  }, []);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!user || !isSupported) return false;

    try {
      setIsRegistering(true);

      const permission = await Notification.requestPermission();
      setPermissionStatus(permission);

      if (permission !== 'granted') {
        console.log('Notification permission denied');
        setIsRegistering(false);
        return false;
      }

      let registration = await navigator.serviceWorker.getRegistration();

      if (!registration) {
        registration = await navigator.serviceWorker.register('/sw.js');
        console.log('Service Worker registered');
      }

      setServiceWorker(registration);

      await navigator.serviceWorker.ready;

      const vapidPublicKey = await getVapidPublicKey();

      if (!vapidPublicKey) {
        console.error('VAPID public key not available');
        setIsRegistering(false);
        return false;
      }

      const pm = (registration as any).pushManager;

      let pushSubscription = await pm.getSubscription();

      if (!pushSubscription) {
        try {
          pushSubscription = await pm.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
          });
          console.log('New push subscription created');
        } catch (subscribeError) {
          console.error('Error creating push subscription:', subscribeError);
          setIsRegistering(false);
          return false;
        }
      }

      setSubscription(pushSubscription);

      const registered = await registerSubscription(user.id, pushSubscription);

      setIsRegistering(false);
      return registered;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      setIsRegistering(false);
      return false;
    }
  }, [user, isSupported]);

  const unsubscribe = useCallback(async (): Promise<boolean> => {
    if (!subscription) return true;

    try {
      await subscription.unsubscribe();
      setSubscription(null);
      console.log('Push subscription unsubscribed');
      return true;
    } catch (error) {
      console.error('Error unsubscribing:', error);
      return false;
    }
  }, [subscription]);

  return {
    isSupported,
    permissionStatus,
    requestPermission,
    unsubscribe,
    isGranted: permissionStatus === 'granted',
    isDenied: permissionStatus === 'denied',
    serviceWorker,
    isRegistering,
    subscription,
    hasSubscription: !!subscription
  };
};
